import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function fmtMonth(year, month) {
  const d = new Date(year, (month || 1) - 1, 1);
  return d.toLocaleDateString("cs-CZ", { month: "long", year: "numeric" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString("cs-CZ");
}

function fmtDateTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("cs-CZ");
}

function getUiStatusMeta(uiStatus) {
  switch (uiStatus) {
    case "submitted":
      return {
        label: "Odesláno ke schválení",
        className: "bg-yellow-50 text-yellow-800 border-yellow-200",
      };
    case "approved":
      return {
        label: "Schváleno",
        className: "bg-green-50 text-green-800 border-green-200",
      };
    case "not-submitted":
    default:
      return {
        label: "Neodesláno",
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
  }
}

function getDeadlineMeta(deadlineStatus) {
  switch (deadlineStatus) {
    case "overdue-submit":
      return {
        label: "Po termínu odeslání",
        className: "bg-orange-50 text-orange-800 border-orange-200",
      };
    case "overdue-approve":
      return {
        label: "Po termínu schválení",
        className: "bg-red-50 text-red-800 border-red-200",
      };
    case "ok":
    default:
      return {
        label: "Včas",
        className: "bg-green-50 text-green-800 border-green-200",
      };
  }
}

function getSeverityMeta(severity) {
  switch (severity) {
    case "critical":
      return {
        label: "Kritické",
        className: "bg-red-50 text-red-800 border-red-200",
      };
    case "operational":
    default:
      return {
        label: "Provozní",
        className: "bg-orange-50 text-orange-800 border-orange-200",
      };
  }
}

function SummaryCard({ title, value, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
      ? "border-orange-200 bg-orange-50 text-orange-800"
      : tone === "success"
      ? "border-green-200 bg-green-50 text-green-800"
      : "border-gray-200 bg-white text-gray-800";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneClass}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function ShiftRisksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const pharmacyCode = searchParams.get("pharmacy") || "";
  const year = Number(searchParams.get("year"));
  const month = Number(searchParams.get("month"));

  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState({
    criticalDays: 0,
    operationalDays: 0,
    totalRiskDays: 0,
    risksEvaluated: false,
  });
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isManager = user?.role === "admin" || user?.role === "rm";

  useEffect(() => {
    if (!user?.firma || !isManager) return;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        if (!pharmacyCode || !Number.isInteger(year) || !Number.isInteger(month)) {
          throw new Error("Chybí parametry pobočky nebo období.");
        }

        const qs = new URLSearchParams({
          firma: user.firma,
          pharmacyCode,
          year: String(year),
          month: String(month),
        });

        const res = await fetch(`/api/shifts/risks?${qs.toString()}`, {
          credentials: "include",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
        }

        setMeta(data?.meta || null);
        setSummary(
          data?.summary || {
            criticalDays: 0,
            operationalDays: 0,
            totalRiskDays: 0,
            risksEvaluated: false,
          }
        );
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        console.error("ShiftRisksPage load error:", e);
        setMeta(null);
        setSummary({
          criticalDays: 0,
          operationalDays: 0,
          totalRiskDays: 0,
          risksEvaluated: false,
        });
        setItems([]);
        setError("Nepodařilo se načíst detail rizik směn.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.firma, isManager, pharmacyCode, year, month]);

  const title = useMemo(() => {
    return pharmacyCode
      ? `Rizika směn – pobočka ${pharmacyCode}`
      : "Rizika směn";
  }, [pharmacyCode]);

  const openPlan = () => {
    if (!pharmacyCode || !year || !month) return;

    const base = user?.role === "admin" ? "/admin/shifts" : "/shifts";

    navigate(
      `${base}?mode=review&pharmacy=${encodeURIComponent(
        pharmacyCode
      )}&year=${year}&month=${month}`
    );
  };

  const goBackToOverview = () => {
    navigate("/admin/personal/shifts-overview");
  };

  if (!isManager) {
    return <div className="p-6">Nemáte oprávnění.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
          <div className="text-sm text-gray-500 mt-1">
            Přehled problémových dnů pro vybranou pobočku a měsíc.
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={goBackToOverview}
            className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
          >
            Zpět na dohled
          </button>

          <button
            onClick={openPlan}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            Otevřít plán
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-2xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Pobočka</div>
            <div className="font-semibold text-gray-800 mt-1">{pharmacyCode || "—"}</div>
          </div>

          <div>
            <div className="text-gray-500">Měsíc</div>
            <div className="font-semibold text-gray-800 mt-1">
              {Number.isInteger(year) && Number.isInteger(month) ? fmtMonth(year, month) : "—"}
            </div>
          </div>

          <div>
            <div className="text-gray-500">Verze</div>
            <div className="font-semibold text-gray-800 mt-1">
              {meta?.version ? `v${meta.version}` : "—"}
            </div>
          </div>

          <div>
            <div className="text-gray-500">Poslední změna</div>
            <div className="font-semibold text-gray-800 mt-1">
              {fmtDateTime(meta?.updatedAt)}
            </div>
          </div>

          <div>
            <div className="text-gray-500">Schváleno</div>
            <div className="font-semibold text-gray-800 mt-1">
              {fmtDateTime(meta?.approvedAt)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <SummaryCard title="Kritické dny" value={summary.criticalDays} tone="danger" />
        <SummaryCard title="Provozní dny" value={summary.operationalDays} tone="warning" />
        <SummaryCard title="Celkem rizikových dnů" value={summary.totalRiskDays} />
        <div className="rounded-2xl border p-4 shadow-sm bg-white">
          <div className="text-sm font-medium text-gray-600">Stav plánu / deadline</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border ${
                getUiStatusMeta(meta?.uiStatus).className
              }`}
            >
              {getUiStatusMeta(meta?.uiStatus).label}
            </span>

            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border ${
                getDeadlineMeta(meta?.deadlineStatus).className
              }`}
            >
              {getDeadlineMeta(meta?.deadlineStatus).label}
            </span>
          </div>
        </div>
      </div>

      {loading && <div className="text-gray-500">Načítám detail rizik…</div>}

      {!loading && error && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4">
          {error}
        </div>
      )}

      {!loading && !error && !summary.risksEvaluated && (
        <div className="rounded-xl bg-white border p-6 text-gray-600 shadow-sm">
          Rizika se vyhodnocují až u plánů, které jsou odeslané ke schválení nebo schválené.
        </div>
      )}

      {!loading && !error && summary.risksEvaluated && items.length === 0 && (
        <div className="rounded-xl bg-white border p-6 text-gray-600 shadow-sm">
          Pro tento plán nebyly nalezeny žádné problémové dny.
        </div>
      )}

      {!loading && !error && summary.risksEvaluated && items.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-2xl shadow border">
          <table className="min-w-[1050px] w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Den</th>
                <th className="text-left px-4 py-3">Magistr</th>
                <th className="text-left px-4 py-3">Asistent</th>
                <th className="text-left px-4 py-3">Problém</th>
                <th className="text-left px-4 py-3">Závažnost</th>
                <th className="text-right px-4 py-3">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const severityMeta = getSeverityMeta(item.severity);

                return (
                  <tr key={item.date} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{fmtDate(item.date)}</td>
                    <td className="px-4 py-3 text-gray-700 capitalize">{item.weekdayLabel}</td>

                    <td className="px-4 py-3">
                      {item.magistrOk ? (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-green-50 text-green-800 border-green-200">
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-red-50 text-red-800 border-red-200">
                          Chybí
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {item.assistantOk ? (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-green-50 text-green-800 border-green-200">
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border bg-orange-50 text-orange-800 border-orange-200">
                          Chybí
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-gray-800">{item.problemLabel}</td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border ${severityMeta.className}`}
                      >
                        {severityMeta.label}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={openPlan}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                          Otevřít plán
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}